import React, { useEffect, useState, useRef } from 'react';
import { Typography, CircularProgress } from '@mui/material';
import { observer } from 'mobx-react';
import normalizeWheel from 'normalize-wheel';
import MSABlock from './MSABlock';
const MSACanvas = observer(function ({ model }) {
    const { MSA, msaFilehandle, height, msaAreaWidth, blocks2d } = model;
    const ref = useRef(null);
    // wheel
    const scheduled = useRef(false);
    const deltaX = useRef(0);
    const deltaY = useRef(0);
    // mouse click-and-drag scrolling
    const prevX = useRef(0);
    const prevY = useRef(0);
    const [mouseDragging, setMouseDragging] = useState(false);
    useEffect(() => {
        const curr = ref.current;
        if (!curr) {
            return;
        }
        function onWheel(origEvent) {
            const event = normalizeWheel(origEvent);
            deltaX.current += event.pixelX;
            deltaY.current += event.pixelY;
            if (!scheduled.current) {
                scheduled.current = true;
                requestAnimationFrame(() => {
                    model.doScrollX(-deltaX.current);
                    model.doScrollY(-deltaY.current);
                    deltaX.current = 0;
                    deltaY.current = 0;
                    scheduled.current = false;
                });
            }
            origEvent.preventDefault();
        }
        curr.addEventListener('wheel', onWheel);
        return () => {
            curr.removeEventListener('wheel', onWheel);
        };
    }, [model]);
    useEffect(() => {
        let cleanup = () => { };
        function globalMouseMove(event) {
            event.preventDefault();
            const currX = event.clientX;
            const currY = event.clientY;
            const distanceX = currX - prevX.current;
            const distanceY = currY - prevY.current;
            if (distanceX || distanceY) {
                // use rAF to make it so multiple event handlers aren't fired per-frame
                // see https://calendar.perfplanet.com/2013/the-runtime-performance-checklist/
                if (!scheduled.current) {
                    scheduled.current = true;
                    window.requestAnimationFrame(() => {
                        model.doScrollX(distanceX);
                        model.doScrollY(distanceY);
                        scheduled.current = false;
                        prevX.current = event.clientX;
                        prevY.current = event.clientY;
                    });
                }
            }
        }
        function globalMouseUp() {
            prevX.current = 0;
            if (mouseDragging) {
                setMouseDragging(false);
            }
        }
        if (mouseDragging) {
            window.addEventListener('mousemove', globalMouseMove, true);
            window.addEventListener('mouseup', globalMouseUp, true);
            cleanup = () => {
                window.removeEventListener('mousemove', globalMouseMove, true);
                window.removeEventListener('mouseup', globalMouseUp, true);
            };
        }
        return cleanup;
    }, [model, mouseDragging]);
    return (React.createElement("div", { ref: ref, onMouseDown: event => {
            // check if clicking a draggable element or a resize handle
            const target = event.target;
            if (target.draggable || target.dataset.resizer) {
                return;
            }
            // otherwise do click and drag scroll
            if (event.button === 0) {
                prevX.current = event.clientX;
                prevY.current = event.clientY;
                setMouseDragging(true);
            }
        }, onMouseUp: event => {
            event.preventDefault();
            setMouseDragging(false);
        }, onMouseLeave: event => {
            event.preventDefault();
        }, style: {
            position: 'relative',
            height,
            width: msaAreaWidth,
            overflow: 'hidden',
        } }, !MSA && !msaFilehandle ? null : !MSA ? (React.createElement(Loading, null)) : (blocks2d.map(([bx, by]) => (React.createElement(MSABlock, { key: `${bx}_${by}`, model: model, offsetX: bx, offsetY: by }))))));
});
function Loading() {
    return (React.createElement("div", { style: {
            position: 'absolute',
            left: '50%',
            top: '50%',
        } },
        React.createElement(CircularProgress, null),
        React.createElement(Typography, null, "Loading...")));
}
export default MSACanvas;
//# sourceMappingURL=MSACanvas.js.map